<template>
  <div class="painel-alertas" data-testid="painel-alertas">
    <div class="painel-header" data-testid="painel-alertas-header">
      <h3>
        <i class="fa fa-bell"></i>
        Painel de Alertas
      </h3>
      <div class="header-info">
        <span v-if="ultimaAtualizacao" class="ultima-atualizacao" data-testid="ultima-atualizacao">
          Última atualização: {{ formatarData(ultimaAtualizacao) }}
        </span>
      </div>
    </div>

    <!-- Cards de Estatísticas -->
    <div class="estatisticas-grid" v-if="dashboard" data-testid="estatisticas-grid">
      <div class="stat-card success" data-testid="stat-card-taxa-sucesso">
        <div class="stat-icon">
          <i class="fa fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <h4 data-testid="taxa-sucesso-valor">{{ dashboard.estatisticas.taxa_sucesso }}%</h4>
          <p>Taxa de Sucesso</p>
        </div>
      </div>

      <div class="stat-card info" data-testid="stat-card-crons-ativos">
        <div class="stat-icon">
          <i class="fa fa-cogs"></i>
        </div>
        <div class="stat-content">
          <h4 data-testid="crons-ativos-valor">{{ dashboard.estatisticas.crons_ativos }}</h4>
          <p>Crons Ativos</p>
        </div>
      </div>

      <div class="stat-card warning" data-testid="stat-card-crons-erro">
        <div class="stat-icon">
          <i class="fa fa-exclamation-triangle"></i>
        </div>
        <div class="stat-content">
          <h4 data-testid="crons-erro-valor">{{ dashboard.estatisticas.crons_com_erro }}</h4>
          <p>Com Erro</p>
        </div>
      </div>

      <div class="stat-card primary" data-testid="stat-card-total-alertas">
        <div class="stat-icon">
          <i class="fa fa-bell"></i>
        </div>
        <div class="stat-content">
          <h4 data-testid="total-alertas-valor">{{ totalAlertas }}</h4>
          <p>Total Alertas</p>
        </div>
      </div>
    </div>

    <!-- Alertas por Tipo -->
    <div class="alertas-section" v-if="dashboard">
      <h4>Alertas por Tipo</h4>
      <div class="alertas-tipo-grid">
        <div v-for="(quantidade, tipo) in dashboard.alertas_por_tipo" :key="tipo" 
             class="tipo-card" :class="getTipoClass(tipo)">
          <div class="tipo-icon">
            <i :class="getTipoIcon(tipo)"></i>
          </div>
          <div class="tipo-content">
            <h5>{{ quantidade }}</h5>
            <p>{{ getTipoNome(tipo) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Crons Críticos -->
    <div class="criticos-section" v-if="dashboard && dashboard.crons_criticos.length > 0">
      <h4>
        <i class="fa fa-exclamation-triangle text-danger"></i>
        Crons Críticos
      </h4>
      <div class="criticos-list">
        <div v-for="cron in dashboard.crons_criticos" :key="cron.comando" class="critico-item">
          <div class="critico-info">
            <strong>{{ cron.nome }}</strong>
            <small>{{ cron.comando }}</small>
            <p class="erro-msg">{{ cron.erro }}</p>
          </div>
          <div class="critico-status">
            <span class="status-badge erro">Erro</span>
            <small v-if="cron.ultima_execucao">
              {{ formatarData(cron.ultima_execucao) }}
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Status dos Crons -->
    <div class="status-section" data-testid="status-crons-section">
      <div class="section-header" data-testid="status-crons-header">
        <h4>Status dos Crons</h4>
        <div class="filtros" data-testid="filtros-container">
          <Select 
            :options="filtroStatusOptions"
            legend=""
            placeholder="Filtrar por status"
            :modelValue="filtroStatus"
            @update:modelValue="$event => filtroStatus = $event"
            size="small"
            data-testid="filtro-status-crons"
          />
        </div>
      </div>
      
      <div class="status-grid" data-testid="status-crons-grid">
        <div v-for="cron in cronsFiltrados" :key="cron.comando" 
             class="status-card" :class="getStatusClass(cron.status)"
             :data-testid="`cron-card-${cron.comando}`">
          <div class="status-header">
            <h5 data-testid="cron-nome">{{ cron.nome }}</h5>
            <span class="status-badge" :class="getStatusClass(cron.status)" 
                  :data-testid="`cron-status-${cron.status}`">
              {{ getStatusTexto(cron.status) }}
            </span>
          </div>
          <div class="status-details">
            <p data-testid="cron-comando"><strong>Comando:</strong> {{ cron.comando }}</p>
            <p v-if="cron.ultima_execucao_formatada" data-testid="cron-ultima-execucao">
              <strong>Última execução:</strong> {{ cron.ultima_execucao_formatada }}
            </p>
            <p v-else-if="cron.status === 'nunca_executado'" data-testid="cron-nunca-executado">
              <strong>Última execução:</strong> <span class="text-muted">Nunca executado</span>
            </p>
            <p v-if="cron.tempo_execucao" data-testid="cron-tempo-execucao">
              <strong>Tempo:</strong> {{ cron.tempo_execucao }}s
            </p>
            <p v-if="cron.erro" class="erro-msg" data-testid="cron-erro">
              <strong>Erro:</strong> {{ cron.erro }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Histórico de Alertas -->
    <div class="historico-section" data-testid="historico-alertas-section">
      <div class="section-header" data-testid="historico-alertas-header">
        <h4>Histórico de Alertas</h4>
        <div class="filtros" data-testid="filtros-historico-container">
          <Select 
            :options="filtroTipoOptions"
            legend=""
            placeholder="Filtrar por tipo"
            :modelValue="filtroTipo"
            @update:modelValue="$event => { filtroTipo = $event; carregarHistorico(); }"
            size="small"
            data-testid="filtro-tipo-historico"
          />
        </div>
      </div>

      <div class="historico-list" v-if="historico.length > 0">
        <div v-for="(item, index) in historico" :key="index" class="historico-item">
          <div class="historico-icon" :class="getNivelClass(item.nivel)">
            <i :class="getNivelIcon(item.nivel)"></i>
          </div>
          <div class="historico-content">
            <p class="historico-mensagem">{{ item.mensagem }}</p>
            <small class="historico-data">{{ item.data_formatada || item.timestamp }}</small>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <i class="fa fa-history fa-2x text-muted"></i>
        <p class="text-muted">Nenhum alerta encontrado no histórico</p>
      </div>
    </div>
  </div>
</template>

<script>
import Button from '../../core/Button.vue'
import Select from '../../core/Select.vue'

export default {
  name: 'PainelAlertas',
  components: {
    Button,
    Select
  },
  data() {
    return {
      carregando: false,
      dashboard: null,
      statusCrons: [],
      historico: [],
      filtroStatus: '',
      filtroTipo: '',
      ultimaAtualizacao: null,
      filtroStatusOptions: [
        { value: '', text: 'Todos os status' },
        { value: 'online', text: 'Online' },
        { value: 'com_erro', text: 'Com erro' },
        { value: 'executando', text: 'Executando' },
        { value: 'nunca_executado', text: 'Nunca executado' }
      ],
      filtroTipoOptions: [
        { value: '', text: 'Todos os tipos' },
        { value: 'itau', text: 'Itaú' },
        { value: 'getran', text: 'Getran' },
        { value: 'sindicato', text: 'Sindicato' },
        { value: 'cron', text: 'Cron' }
      ]
    }
  },
  computed: {
    totalAlertas() {
      if (!this.dashboard || !this.dashboard.estatisticas) return 0;
      return this.dashboard.estatisticas.total_alertas || 0;
    },
    cronsFiltrados() {
      if (!this.statusCrons) return [];
      if (!this.filtroStatus) return this.statusCrons;
      return this.statusCrons.filter(cron => cron.status === this.filtroStatus);
    }
  },
  methods: {
    async carregarDados() {
      this.carregando = true;
      this.$emit('loading', true);
      
      try {
        // Força limpeza do cache das requisições
        const timestamp = new Date().getTime();
        
        await Promise.all([
          this.carregarDashboard(timestamp),
          this.carregarStatusCrons(timestamp),
          this.carregarHistorico(timestamp)
        ]);
        
        // Atualiza timestamp da última atualização
        this.ultimaAtualizacao = new Date();
        
        this.$emit('loaded');
        
      } catch (error) {
        this.$emit('error', 'Erro ao carregar dados: ' + error.message);
        console.error('Erro ao carregar dados do painel:', error);
        this.$toasted.show('Erro ao carregar dados: ' + error.message, {
          position: 'top-right',
          type: 'error'
        });
      } finally {
        this.carregando = false;
        this.$emit('loading', false);
      }
    },

    async carregarDashboard(timestamp = null) {
      try {
        const url = timestamp 
          ? `${process.env.VUE_APP_URL_API_V2}/monitoramento/dashboard?_t=${timestamp}`
          : `${process.env.VUE_APP_URL_API_V2}/monitoramento/dashboard`;
          
        const response = await this.$http.get(url);
        if (response.body && response.body.sucesso) {
          this.dashboard = response.body.dados;
        } else {
          console.warn('Dashboard response inválida:', response.body);
          this.dashboard = {
            estatisticas: { taxa_sucesso: 100, crons_ativos: 0, crons_com_erro: 0, total_alertas: 0 },
            alertas_por_tipo: {},
            crons_criticos: []
          };
        }
      } catch (error) {
        console.error('Erro ao carregar dashboard:', error);
        this.dashboard = {
          estatisticas: { taxa_sucesso: 100, crons_ativos: 0, crons_com_erro: 0, total_alertas: 0 },
          alertas_por_tipo: {},
          crons_criticos: []
        };
      }
    },

    async carregarStatusCrons(timestamp = null) {
      try {
        const url = timestamp 
          ? `${process.env.VUE_APP_URL_API_V2}/monitoramento/status-crons?_t=${timestamp}`
          : `${process.env.VUE_APP_URL_API_V2}/monitoramento/status-crons`;
          
        const response = await this.$http.get(url);
        if (response.body && response.body.sucesso) {
          this.statusCrons = response.body.dados || [];
        } else {
          console.warn('Status crons response inválida:', response.body);
          this.statusCrons = [];
        }
      } catch (error) {
        console.error('Erro ao carregar status crons:', error);
        this.statusCrons = [];
      }
    },

    async carregarHistorico(timestamp = null) {
      try {
        let params = this.filtroTipo ? `?tipo=${this.filtroTipo}` : '';
        if (timestamp) {
          params += (params ? '&' : '?') + `_t=${timestamp}`;
        }
        
        const response = await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/monitoramento/historico${params}`);
        if (response.body && response.body.sucesso) {
          this.historico = response.body.dados || [];
        } else {
          console.warn('Histórico response inválida:', response.body);
          this.historico = [];
        }
      } catch (error) {
        console.error('Erro ao carregar histórico:', error);
        this.historico = [];
      }
    },

    async atualizarDados() {
      return await this.carregarDados();
    },

    getTipoClass(tipo) {
      const classes = {
        itau: 'tipo-itau',
        getran: 'tipo-getran', 
        sindicato: 'tipo-sindicato',
        cron: 'tipo-cron',
        outros: 'tipo-outros'
      };
      return classes[tipo] || 'tipo-default';
    },

    getTipoIcon(tipo) {
      const icons = {
        itau: 'fa fa-university',
        getran: 'fa fa-car',
        sindicato: 'fa fa-building',
        cron: 'fa fa-clock',
        outros: 'fa fa-question-circle'
      };
      return icons[tipo] || 'fa fa-bell';
    },

    getTipoNome(tipo) {
      const nomes = {
        itau: 'Itaú',
        getran: 'Getran',
        sindicato: 'Sindicato',
        cron: 'Cron',
        outros: 'Outros'
      };
      return nomes[tipo] || tipo;
    },

    getStatusClass(status) {
      const classes = {
        online: 'success',
        com_erro: 'danger',
        executando: 'warning',
        nunca_executado: 'secondary'
      };
      return classes[status] || 'secondary';
    },

    getStatusTexto(status) {
      const textos = {
        online: 'Online',
        com_erro: 'Com Erro',
        executando: 'Executando',
        nunca_executado: 'Nunca Executado'
      };
      return textos[status] || status;
    },

    getNivelClass(nivel) {
      const classes = {
        error: 'danger',
        warning: 'warning',
        info: 'info',
        debug: 'secondary'
      };
      return classes[nivel] || 'info';
    },

    getNivelIcon(nivel) {
      const icons = {
        error: 'fa fa-times-circle',
        warning: 'fa fa-exclamation-triangle',
        info: 'fa fa-info-circle',
        debug: 'fa fa-bug'
      };
      return icons[nivel] || 'fa fa-info-circle';
    },

    formatarData(data) {
      if (!data) return '';
      try {
        return new Date(data).toLocaleString('pt-BR');
      } catch {
        return data;
      }
    }
  },

  mounted() {
    this.carregarDados();
    // Atualiza dados a cada 30 segundos
    this.intervalId = setInterval(() => {
      this.carregarDados();
    }, 30000);
  },

  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}
</script>

<style lang="scss" scoped>
.painel-alertas {
  padding: 20px;
  background: transparent;
  height: 100%;
  overflow: visible !important;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  /* Não criar contexto de empilhamento */
  transform: none !important;
  will-change: auto !important;
  contain: none !important;
  isolation: auto !important;
}

.painel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  background: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  flex-shrink: 0;

  h3 {
    margin: 0;
    color: #333;
    font-size: 1.1rem;
    
    i {
      margin-right: 6px;
      color: #007bff;
    }
  }
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ultima-atualizacao {
  font-size: 0.85rem;
  color: #6c757d;
  font-style: italic;
}

.estatisticas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
  flex-shrink: 0;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  border-left: 4px solid;

  &.success { border-left-color: #28a745; }
  &.info { border-left-color: #17a2b8; }
  &.warning { border-left-color: #ffc107; }
  &.primary { border-left-color: #007bff; }

  .stat-icon {
    font-size: 2.5rem;
    margin-right: 15px;
    opacity: 0.8;

    .success & { color: #28a745; }
    .info & { color: #17a2b8; }
    .warning & { color: #ffc107; }
    .primary & { color: #007bff; }
  }

  .stat-content {
    h4 {
      margin: 0;
      font-size: 2rem;
      font-weight: bold;
      color: #333;
    }

    p {
      margin: 5px 0 0;
      color: #666;
      font-size: 0.9rem;
    }
  }
}

.alertas-section, .criticos-section, .status-section, .historico-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  margin-bottom: 20px;
  overflow: visible !important;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  /* Garante que não limite o dropdown */
  transform: none !important;
  will-change: auto !important;
  contain: none !important;
  isolation: auto !important;

  h4 {
    margin: 0 0 16px;
    color: #2c3e50;
    border-bottom: 2px solid #e9ecef;
    padding-bottom: 12px;
    font-size: 1.1rem;
    font-weight: 600;
  }
}

/* Status dos Crons tem z-index maior que Histórico */
.status-section {
  z-index: 3 !important;
}

/* Histórico tem z-index menor */
.historico-section {
  z-index: 2 !important;
}

/* Alertas e Críticos mantém z-index padrão */
.alertas-section, .criticos-section {
  z-index: 1 !important;
}

.alertas-tipo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.tipo-card {
  padding: 15px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  text-align: center;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }

  &.tipo-itau { background: #e3f2fd; color: #1976d2; }
  &.tipo-getran { background: #f3e5f5; color: #7b1fa2; }
  &.tipo-sindicato { background: #e8f5e8; color: #388e3c; }
  &.tipo-cron { background: #fff3e0; color: #f57c00; }
  &.tipo-outros { background: #f5f5f5; color: #616161; }

  .tipo-icon {
    font-size: 1.5rem;
    margin-right: 10px;
  }

  .tipo-content {
    h5 {
      margin: 0;
      font-size: 1.5rem;
      font-weight: bold;
    }

    p {
      margin: 0;
      font-size: 0.8rem;
      opacity: 0.8;
    }
  }
}

.criticos-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.critico-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: #ffeaa7;
  border-left: 4px solid #e17055;
  border-radius: 4px;

  .critico-info {
    flex: 1;

    strong {
      color: #2d3436;
      display: block;
    }

    small {
      color: #636e72;
      font-family: monospace;
    }

    .erro-msg {
      margin: 5px 0 0;
      color: #d63031;
      font-size: 0.9rem;
    }
  }

  .critico-status {
    text-align: right;

    .status-badge {
      background: #d63031;
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 0.8rem;
    }

    small {
      display: block;
      margin-top: 5px;
      color: #636e72;
    }
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
  z-index: auto;
  /* Adiciona espaço extra abaixo para o dropdown */
  padding-bottom: 10px;
  /* Não limitar o dropdown */
  overflow: visible !important;

  .filtros {
    min-width: 200px;
    position: relative;
    z-index: auto;
    overflow: visible !important;
  }
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 18px;
  max-height: 450px;
  overflow-y: auto;
  overflow-x: visible;
}

.status-card {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 15px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &.success {
    border-left: 4px solid #28a745;
    background: #f8fff9;
  }

  &.danger {
    border-left: 4px solid #dc3545;
    background: #fffbfb;
  }

  &.warning {
    border-left: 4px solid #ffc107;
    background: #fffef7;
  }

  &.secondary {
    border-left: 4px solid #6c757d;
    background: #f8f9fa;
  }

  .status-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    h5 {
      margin: 0;
      color: #333;
    }

    .status-badge {
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 0.8rem;
      font-weight: bold;

      &.success {
        background: #28a745;
        color: white;
      }

      &.danger {
        background: #dc3545;
        color: white;
      }

      &.warning {
        background: #ffc107;
        color: #333;
      }

      &.secondary {
        background: #6c757d;
        color: white;
      }
    }
  }

  .status-details {
    p {
      margin: 5px 0;
      font-size: 0.9rem;
      color: #666;

      strong {
        color: #333;
      }

      &.erro-msg {
        color: #dc3545;
        background: #f8d7da;
        padding: 8px;
        border-radius: 4px;
        margin-top: 10px;
      }
    }
  }
}

.historico-list {
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.historico-item {
  display: flex;
  align-items: flex-start;
  padding: 10px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8f9fa;
  }

  .historico-icon {
    margin-right: 15px;
    padding: 8px;
    border-radius: 50%;
    flex-shrink: 0;

    &.danger {
      background: #f8d7da;
      color: #721c24;
    }

    &.warning {
      background: #fff3cd;
      color: #856404;
    }

    &.info {
      background: #d1ecf1;
      color: #0c5460;
    }

    &.secondary {
      background: #e2e3e5;
      color: #383d41;
    }
  }

  .historico-content {
    flex: 1;

    .historico-mensagem {
      margin: 0 0 5px;
      color: #333;
      line-height: 1.4;
    }

    .historico-data {
      color: #6c757d;
      font-size: 0.8rem;
    }
  }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;

  i {
    margin-bottom: 15px;
  }
}

.text-muted {
  color: #6c757d !important;
}

.text-danger {
  color: #dc3545 !important;
}

/* Scrollbars customizadas */
.painel-alertas::-webkit-scrollbar,
.status-grid::-webkit-scrollbar,
.historico-list::-webkit-scrollbar {
  width: 6px;
}

.painel-alertas::-webkit-scrollbar-track,
.status-grid::-webkit-scrollbar-track,
.historico-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.painel-alertas::-webkit-scrollbar-thumb,
.status-grid::-webkit-scrollbar-thumb,
.historico-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.painel-alertas::-webkit-scrollbar-thumb:hover,
.status-grid::-webkit-scrollbar-thumb:hover,
.historico-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Responsividade */
@media (max-width: 1200px) {
  .estatisticas-grid {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px;
  }

  .alertas-tipo-grid {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 14px;
  }

  .status-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 14px;
  }
}

@media (max-width: 768px) {
  .painel-alertas {
    padding: 16px;
    gap: 16px;
  }

  .painel-header {
    flex-direction: column;
    gap: 10px;
    text-align: center;
    margin-bottom: 20px;
    padding: 15px;
  }

  .section-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .estatisticas-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 20px;
  }

  .stat-card {
    padding: 15px;
    
    .stat-icon {
      font-size: 2rem;
      margin-right: 10px;
    }
    
    .stat-content h4 {
      font-size: 1.5rem;
    }
  }

  .status-grid {
    grid-template-columns: 1fr;
    max-height: 300px;
  }

  .alertas-tipo-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .tipo-card {
    padding: 10px;
    flex-direction: column;
    text-align: center;
    
    .tipo-icon {
      margin-right: 0;
      margin-bottom: 5px;
      font-size: 1.2rem;
    }
  }

  .critico-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
  }
  
  .alertas-section, .criticos-section, .status-section, .historico-section {
    padding: 12px;
    margin-bottom: 15px;
  }
  
  .historico-list {
    max-height: 200px;
  }
  
  .historico-item {
    padding: 8px;
  }
}

@media (max-width: 480px) {
  .painel-alertas {
    padding: 8px;
    max-height: 65vh;
  }
  
  .estatisticas-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .alertas-tipo-grid {
    grid-template-columns: 1fr;
  }
  
  .stat-card {
    padding: 12px;
    
    .stat-icon {
      font-size: 1.8rem;
    }
    
    .stat-content h4 {
      font-size: 1.3rem;
    }
  }
  
  .tipo-card {
    padding: 12px;
    
    .tipo-content h5 {
      font-size: 1.2rem;
    }
  }
  
  .status-card {
    padding: 12px;
  }
  
  .alertas-section, .criticos-section, .status-section, .historico-section {
    padding: 10px;
    
    h4 {
      font-size: 1rem;
    }
  }
}
</style>
